"use client";

import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useRouter } from "next/navigation";

import {
  ROLE_CONFIGS,
  ROLE_LIST,
  createInitialEntitySelections,
  findEntityOption,
  type RoleConfig,
  type RoleEntityOption,
  type RoleKey,
} from "@/lib/role-config";

type RoleContextValue = {
  roleKey: RoleKey;
  roleConfig: RoleConfig;
  setRole: (role: RoleKey) => void;
  roles: RoleConfig[];
  entityOptionId: string;
  entityOption: RoleEntityOption;
  setEntityOption: (optionId: string) => void;
};

const RoleContext = createContext<RoleContextValue | undefined>(undefined);

export function RoleProvider({ children }: { children: React.ReactNode }) {
  const [roleKey, setRoleKey] = useState<RoleKey>("facilityNonMedical");
  const [entitySelections, setEntitySelections] = useState<Record<RoleKey, string>>(
    createInitialEntitySelections(),
  );
  const router = useRouter();

  const setRole = useCallback(
    (nextRole: RoleKey) => {
      if (nextRole === roleKey) {
        return;
      }
      setRoleKey(nextRole);
      router.push("/new-home");
    },
    [roleKey, router],
  );

  const roleConfig = ROLE_CONFIGS[roleKey];

  const entityOptionId = entitySelections[roleKey] ?? roleConfig.entity.initialOptionId;

  const entityOption = useMemo(() => {
    return (
      findEntityOption(roleConfig, entityOptionId) ??
      findEntityOption(roleConfig, roleConfig.entity.initialOptionId) ??
      roleConfig.entity.groups[0]?.options[0]
    );
  }, [entityOptionId, roleConfig]);

  const setEntityOption = useCallback(
    (optionId: string) => {
      setEntitySelections((prev) => ({ ...prev, [roleKey]: optionId }));
    },
    [roleKey],
  );

  const value = useMemo<RoleContextValue>(
    () => ({
      roleKey,
      roleConfig,
      setRole,
      roles: ROLE_LIST,
      entityOptionId,
      entityOption: entityOption ?? roleConfig.entity.groups[0].options[0],
      setEntityOption,
    }),
    [roleKey, roleConfig, setRole, entityOptionId, entityOption, setEntityOption],
  );

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
}

export function useRole() {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error("useRole must be used within a RoleProvider");
  }
  return context;
}
